import { useState, useCallback, useMemo } from 'react'
import { MobileAction, MobileEventFlow, Variable, MOBILE_EVENT_CODE } from '@mobileTypes'
import { MOBILE_EVENT_CONTEXTS, EventContextParam } from '@mobileConstants'
import { v4 as uuidv4 } from 'uuid'
import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

import { ActionConfigDialog } from './ActionConfigDialog'
import { EventFlowConfigDialog } from './EventFlowConfigDialog'
import { isFlowForElement, type EventFlowElementRef } from './EventFlowsForElementView'
import { useMobileGlobalVariables } from '@mobileHooks'

export interface EventFlowsEditorProps {
  eventFlows: MobileEventFlow[]
  onEventFlowsChange: (flows: MobileEventFlow[]) => void
  formElements?: any[]
  drawerItems?: { internalId?: string; label?: string }[]
  allowedEventCodes?: MOBILE_EVENT_CODE[]
  /** When set, only show flows for this element and pre-fill new flow with this element */
  filterByElementRef?: EventFlowElementRef
  /** Pre-fill when creating new flow (e.g. from element modal) */
  defaultValuesForNewFlow?: {
    eventCode?: MOBILE_EVENT_CODE
    drawerItemIndex?: number
    elementInternalId?: string
    fieldInternalId?: string
    elementId?: string
  }
  /** Show "Add Listener" button (default true) */
  showAddListenerButton?: boolean
  /** Local variables for ActionConfigDialog (form/listItem) */
  variables?: Array<{ internalId: string }>
  /** Global variables for ActionConfigDialog */
  globalVariables?: Array<{ internalId: string }>
  backgroundVariables?: Array<{ internalId: string }>
  sheetFormVariables?: Array<{ internalId: string }>
  /** Create variable callback (local or global) */
  onCreateVariable?: (name: string, isGlobal: boolean, valueType?: string) => void
}

export const EventFlowsEditor = ({
  eventFlows,
  onEventFlowsChange,
  formElements = [],
  drawerItems = [],
  allowedEventCodes,
  filterByElementRef,
  defaultValuesForNewFlow,
  showAddListenerButton = true,
  variables: variableOptions = [],
  globalVariables: globalVariableOptionsProp,
  backgroundVariables: backgroundVariableOptions,
  sheetFormVariables: sheetFormVariableOptions,
  onCreateVariable,
}: EventFlowsEditorProps) => {
  const { variables: globalVarsFromHook, createGlobalVariable } = useMobileGlobalVariables()
  const globalVariableOptions = globalVariableOptionsProp ?? globalVarsFromHook.map((v: any) => ({ internalId: v.internalId }))

  // ActionConfigDialog expects variables with internalId
  const variablesForActionDialog = useMemo(
    () =>
      variableOptions.map((v: { internalId?: string }) => ({
        ...v,
        internalId: v.internalId ?? '',
      })),
    [variableOptions]
  )
  const globalVariablesForActionDialog = useMemo(
    () =>
      globalVariableOptions.map((v: { internalId?: string }) => ({
        ...v,
        internalId: v.internalId ?? '',
      })),
    [globalVariableOptions]
  )

  const [eventFlowDialogOpen, setEventFlowDialogOpen] = useState(false)
  const [actionDialogOpen, setActionDialogOpen] = useState(false)
  const [selectedEventFlow, setSelectedEventFlow] = useState<MobileEventFlow | null>(null)
  const [selectedAction, setSelectedAction] = useState<MobileAction | null>(null)
  const [editingActionFlowInternalId, setEditingActionFlowInternalId] = useState<string | null>(null)

  const displayedFlows = filterByElementRef
    ? eventFlows.filter(f => isFlowForElement(f, filterByElementRef))
    : eventFlows

  const handleSaveEventFlow = useCallback(
    (flow: MobileEventFlow) => {
      const idx = eventFlows.findIndex(f => f.internalId === flow.internalId)
      const newFlows = [...eventFlows]
      if (idx >= 0) {
        newFlows[idx] = flow
      } else {
        newFlows.push(flow)
      }
      onEventFlowsChange(newFlows)
    },
    [eventFlows, onEventFlowsChange]
  )

  const handleDeleteEventFlow = useCallback(
    (flowInternalId: string) => {
      onEventFlowsChange(eventFlows.filter(f => f.internalId !== flowInternalId))
    },
    [eventFlows, onEventFlowsChange]
  )

  const handleSaveAction = useCallback(
    (action: MobileAction) => {
      if (editingActionFlowInternalId == null) return
      const flowIndex = eventFlows.findIndex(f => f.internalId === editingActionFlowInternalId)
      if (flowIndex < 0) return
      const newFlows = [...eventFlows]
      const flow = newFlows[flowIndex]
      const existingIndex = flow.actions.findIndex((a: MobileAction) => a.internalId === action.internalId)
      if (existingIndex !== -1) {
        flow.actions[existingIndex] = action
      } else {
        flow.actions.push(action)
      }
      onEventFlowsChange(newFlows)
    },
    [eventFlows, editingActionFlowInternalId, onEventFlowsChange]
  )

  const handleDeleteAction = useCallback(
    (flowInternalId: string, actionIndex: number) => {
      const flowIndex = eventFlows.findIndex(f => f.internalId === flowInternalId)
      if (flowIndex < 0) return
      const newFlows = [...eventFlows]
      newFlows[flowIndex].actions.splice(actionIndex, 1)
      onEventFlowsChange(newFlows)
    },
    [eventFlows, onEventFlowsChange]
  )

  const handleCreateVariable = useCallback(
    (variableInternalId: string, isGlobal: boolean = false, valueType: string = 'string') => {
      if (!onCreateVariable) {
        if (isGlobal) {
          createGlobalVariable({
            internalId: variableInternalId.trim(),
            variableType: 'single',
            valueType: valueType || 'string',
            defaultValue: undefined,
          })
        }
        return
      }
      onCreateVariable(variableInternalId, isGlobal, valueType)
    },
    [onCreateVariable, createGlobalVariable]
  )

  const editingFlowFullIndex = selectedEventFlow != null ? eventFlows.findIndex(f => f.internalId === selectedEventFlow.internalId) : -1
  const isEditingExistingFlow = selectedEventFlow != null && editingFlowFullIndex >= 0

  const handleAddEventFlow = () => {
    setSelectedEventFlow(null)
    setEventFlowDialogOpen(true)
  }

  const handleEditEventFlow = (flow: MobileEventFlow) => {
    setSelectedEventFlow(flow)
    setEventFlowDialogOpen(true)
  }

  const handleAddAction = (flowInternalId: string) => {
    setSelectedAction(null)
    setEditingActionFlowInternalId(flowInternalId)
    setActionDialogOpen(true)
  }

  const handleEditAction = (action: MobileAction, flowInternalId: string) => {
    setSelectedAction(action)
    setEditingActionFlowInternalId(flowInternalId)
    setActionDialogOpen(true)
  }

  const effectiveDefaultValues =
    defaultValuesForNewFlow ??
    (filterByElementRef?.type === 'drawer_item'
      ? {
          eventCode: MOBILE_EVENT_CODE.ON_DRAWER_ITEM_PRESS as const,
          drawerItemInternalId: filterByElementRef.drawerItemInternalId,
        }
      : filterByElementRef?.type === 'button'
        ? { eventCode: MOBILE_EVENT_CODE.ON_BUTTON_PRESS as const, elementInternalId: filterByElementRef.elementInternalId }
        : filterByElementRef?.type === 'swipe'
          ? { eventCode: MOBILE_EVENT_CODE.ON_BUTTON_SWIPE as const, elementInternalId: filterByElementRef.elementInternalId }
          : filterByElementRef?.type === 'field'
            ? { eventCode: MOBILE_EVENT_CODE.ON_FIELD_VALUE_CHANGE as const, fieldInternalId: filterByElementRef.fieldInternalId }
            : filterByElementRef?.type === 'embedded_list'
              ? { eventCode: MOBILE_EVENT_CODE.ON_CHECKBOX_EMBEDDED_LIST_CHANGE as const, elementId: filterByElementRef.elementId }
              : undefined)

  return (
    <Box>
      {showAddListenerButton && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant='h6'>Event Flows</Typography>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAddEventFlow}
            size='small'
          >
            Add Listener
          </Button>
        </Box>
      )}

      {displayedFlows.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color='text.secondary'>
            No event flows configured.{showAddListenerButton ? ' Click "Add Listener" to create one.' : ''}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {displayedFlows.map((flow: MobileEventFlow) => (
            <Card key={flow.internalId} variant='outlined'>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                      Event: {flow.eventCode}
                      {flow.elementInternalId && (
                        <Chip label={`#${flow.elementInternalId}`} size='small' sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {flow.fieldInternalId && (
                        <Chip label={`#${flow.fieldInternalId}`} size='small' sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {flow.elementId && (
                        <Chip label={`#${flow.elementId}`} size='small' sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {flow.intervalId && (
                        <Chip label={`⏱ ${flow.intervalId}`} size='small' sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                      )}
                      {flow.conditionBranchId && (
                        <Chip
                          label={`branch: ${flow.conditionBranchId}`}
                          size='small'
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          color='secondary'
                        />
                      )}
                      {flow.eventCode === MOBILE_EVENT_CODE.ON_VARIABLE_CHANGE && flow.variableName && (
                        <Chip
                          label={`${
                            flow.variableScope === 'background'
                              ? `background: ${flow.variableName}`
                              : flow.variableScope === 'sheetForm'
                                ? `sheetForm: ${flow.variableName}`
                                : flow.variableScope === 'global'
                                  ? `global: ${flow.variableName}`
                                  : flow.variableName
                          }`}
                          size='small'
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      {flow.drawerItemIndex != null && drawerItems[flow.drawerItemIndex] && (
                        <Chip
                          label={`Drawer #${flow.drawerItemIndex + 1}${drawerItems[flow.drawerItemIndex]?.label ? `: ${drawerItems[flow.drawerItemIndex].label}` : ''}`}
                          size='small'
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Flow Internal ID: {flow.internalId}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton size='small' onClick={() => handleEditEventFlow(flow)} color='primary'>
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleDeleteEventFlow(flow.internalId)} color='error'>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  {MOBILE_EVENT_CONTEXTS[flow.eventCode]?.availableParams?.length > 0 && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 1,
                        px: 1,
                        py: 0.5,
                        bgcolor: 'primary.50',
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography variant='caption' fontWeight='bold' color='primary.main'>
                        Context:
                      </Typography>
                      {MOBILE_EVENT_CONTEXTS[flow.eventCode].availableParams.map((param: EventContextParam) => (
                        <Chip
                          key={param.name}
                          label={`eventData.${param.name}: ${param.type}`}
                          size='small'
                          sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'primary.main', color: 'white' }}
                        />
                      ))}
                    </Box>
                  )}
                  {flow.actions.length === 0 ? (
                    <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                      No actions configured
                    </Typography>
                  ) : (
                    <Stack direction='row' spacing={1} flexWrap='wrap' sx={{ gap: 1 }}>
                      {flow.actions.map((action: MobileAction, actionIndex: number) => {
                        const hasNoDependencies = !action.dependsOn || action.dependsOn.length === 0
                        const isParallel =
                          hasNoDependencies &&
                          flow.actions.filter((a: MobileAction) => !a.dependsOn || a.dependsOn.length === 0).length > 1
                        return (
                          <Box key={action.internalId} sx={{ display: 'flex', alignItems: 'center' }}>
                            {actionIndex > 0 && !hasNoDependencies && (
                              <ArrowForwardIcon fontSize='small' sx={{ mx: 0.5, color: 'text.secondary' }} />
                            )}
                            <Chip
                              label={
                                <Box>
                                  <Typography variant='caption' display='block'>
                                    {action.actionCode}
                                  </Typography>
                                  {(action.meta?.command || (action as any).command) && (
                                    <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                                      📦 {(action.meta?.command || (action as any).command).objectCode}:
                                      {(action.meta?.command || (action as any).command).name}
                                    </Typography>
                                  )}
                                  {(action.meta?.screenName || (action as any).screenName) && (
                                    <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                                      → {action.meta?.screenName || (action as any).screenName}
                                      {(action.meta?.navigationParams || (action as any).navigationParams) &&
                                        ` (${action.meta?.navigationParams || (action as any).navigationParams})`}
                                    </Typography>
                                  )}
                                  {(action.meta?.variableName || (action as any).variableName) && (
                                    <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                                      🔧 {action.meta?.variableName || (action as any).variableName}
                                    </Typography>
                                  )}
                                  {action.meta?.intervalId && (
                                    <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                                      ⏱ {action.meta?.intervalId}
                                      {action.meta?.intervalMs && ` (${action.meta?.intervalMs}ms)`}
                                    </Typography>
                                  )}
                                  {isParallel && (
                                    <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                                      parallel
                                    </Typography>
                                  )}
                                  {action.dependsOn && action.dependsOn.length > 0 && (
                                    <Typography variant='caption' display='block' sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                                      depends: {action.dependsOn.join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              onClick={() => handleEditAction(action, flow.internalId)}
                              onDelete={() => handleDeleteAction(flow.internalId, actionIndex)}
                              sx={{ height: 'auto', py: 0.5 }}
                            />
                          </Box>
                        )
                      })}
                    </Stack>
                  )}
                </Box>

                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<AddIcon />}
                  onClick={() => handleAddAction(flow.internalId)}
                >
                  Add Action
                </Button>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <EventFlowConfigDialog
        open={eventFlowDialogOpen}
        eventFlow={selectedEventFlow}
        formElements={formElements}
        drawerItems={drawerItems}
        allowedEventCodes={allowedEventCodes}
        variables={variableOptions}
        globalVariables={globalVariableOptions}
        backgroundVariables={backgroundVariableOptions}
        sheetFormVariables={sheetFormVariableOptions}
        defaultValues={!isEditingExistingFlow ? effectiveDefaultValues : undefined}
        lockElementSelection={
          !isEditingExistingFlow && filterByElementRef != null
        }
        onClose={() => setEventFlowDialogOpen(false)}
        onSave={flow => {
          handleSaveEventFlow(flow)
          setEventFlowDialogOpen(false)
        }}
      />

      {editingActionFlowInternalId != null && (
        <ActionConfigDialog
          open={actionDialogOpen}
          action={selectedAction}
          existingActions={eventFlows.find(f => f.internalId === editingActionFlowInternalId)?.actions ?? []}
          eventCode={eventFlows.find(f => f.internalId === editingActionFlowInternalId)?.eventCode}
          formElements={formElements}
          variables={variablesForActionDialog}
          globalVariables={globalVariablesForActionDialog}
          backgroundVariables={backgroundVariableOptions}
          sheetFormVariables={sheetFormVariableOptions}
          eventFlows={eventFlows}
          onClose={() => {
            setActionDialogOpen(false)
            setEditingActionFlowInternalId(null)
          }}
          onSave={action => {
            handleSaveAction(action)
            setActionDialogOpen(false)
            setEditingActionFlowInternalId(null)
          }}
          onCreateVariable={handleCreateVariable}
        />
      )}
    </Box>
  )
}
